import React from "react";
import styled from "styled-components";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";

const StyledTableContainer = styled(TableContainer)`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin: 0;
  width: 100%;

  @media (max-width: 768px) {
    margin: 0;
    border-radius: 8px;
  }
`;

// Updated TableCard with better responsive behavior
const TableCard = styled(Box)`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 16px;
  margin-bottom: 24px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 16px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    margin-bottom: 12px;
  }
`;

const HeaderCell = styled(TableCell)`
  font-weight: 700 !important;
  color: #333;
  font-size: 14px;
  background: #f8f8f8;
  border-bottom: 1px solid #e0e0e0;
  font-family: "Urbanist";
  white-space: nowrap;
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px 12px;
  }

  @media (max-width: 600px) {
    font-size: 12px;
    padding: 8px 8px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 6px 4px;
  }
`;

const StyledTableCell = styled(TableCell)`
  font-size: 14px;
  color: #222;
  border-bottom: 1px solid #f0f0f0;
  font-family: "Urbanist";
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px 12px;
  }

  @media (max-width: 600px) {
    font-size: 12px;
    padding: 8px 8px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 6px 4px;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-family: "Urbanist";

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 600px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const ActionButton = styled(Button)`
  border-radius: 24px !important;
  padding: 4px 18px !important;
  text-transform: none !important;
  font-weight: 500;
  font-size: 12px;
  font-family: "Urbanist";
  min-width: auto !important;
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  
  ${({ variant }) =>
    variant === "delete"
      ? `
    color: #DC2626 !important;
    background: transparent !important;
    padding: 4px 12px !important;
  `
    : variant === "view"
      ? `
    color: #2B73EF !important;
    background: transparent !important;
  `
    : variant === "viewDetails"
      ? `
    border: 1.5px solid #12188a !important;
    color: #12188a !important;
    background-color: #EDEEFF !important;
  `
    : ''}

  @media (max-width: 768px) {
    padding: 3px 14px !important;
    font-size: 11px;
  }

  @media (max-width: 600px) {
    padding: 2px 12px !important;
    font-size: 10px;
  }
`;

const ResponsiveTable = styled(Table)`
  width: 100%;
  table-layout: auto;

  @media (max-width: 768px) {
    min-width: 600px;
  }

  @media (max-width: 480px) {
    min-width: 500px;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
`;

// Add a wrapper for horizontal scroll on mobile
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    &::-webkit-scrollbar {
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
`;

const BossesTable = ({
  data,
  columns,
  onActionClick,
  actionLabel = "",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const renderCell = (item, column) => {
    if (column.renderer) {
      return column.renderer(item[column.key], item);
    }

    if (column.key === "action") {
      const getVariant = () => {
        if (actionLabel === "Delete") return "delete";
        if (actionLabel === "View") return "view";
        return "viewDetails"; // This will be used for "View Details"
      };
      
      return (
        <ActionButton
          variant={getVariant()}
          onClick={() => onActionClick(item)}
        >
          {actionLabel}
        </ActionButton>
      );
    }

    return item[column.key];
  };

  return (
    <TableCard>
      <TableWrapper>
        <StyledTableContainer component={Paper}>
          <ResponsiveTable>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <HeaderCell 
                    key={column.key}
                    align={column.key === 'action' ? 'center' : 'left'}
                  >
                    {column.label}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, rowIndex) => (
                <TableRow key={item._id || rowIndex}>
                  {columns.map((column) => (
                    <StyledTableCell 
                      key={column.key}
                      align={column.key === 'action' ? 'center' : 'left'}
                    >
                      {renderCell(item, column)}
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        </StyledTableContainer>
      </TableWrapper>
    </TableCard>
  );
};

export default BossesTable;